import React from 'react';
import { Space, Button, Avatar, Badge } from 'antd';
import { PictureOutlined, CloseCircleTwoTone } from '@ant-design/icons';
import MediaPicker from './MediaPicker';
import { MediaUploadConfig } from '../../utils/file.util';
import { MediaFile } from '../../hooks/use-media.hook';
import { useTranslation } from 'react-i18next';

interface MediaPickerFieldProps {
  value?: string[];
  onChange?: (value: string[]) => void;
  uploadConfig: MediaUploadConfig;
  disabled?: boolean;
  maxItems?: number;
}

const MediaPickerField: React.FC<MediaPickerFieldProps> = ({
  value = [],
  onChange,
  uploadConfig,
  disabled = false,
  maxItems,
}) => {
  const { t } = useTranslation();

  const handleFilesSelect = (files: MediaFile[]) => {
    if (!onChange) return;

    const newUrls = files.map((file) => file.url);
    const updatedUrls = [...value, ...newUrls];

    // If maxItems is set, only take the first maxItems
    const finalUrls = maxItems ? updatedUrls.slice(0, maxItems) : updatedUrls;
    onChange(finalUrls);
  };

  const handleRemove = (urlToRemove: string) => {
    if (!onChange) return;
    onChange(value.filter((url) => url !== urlToRemove));
  };

  const canAddMore = !maxItems || value.length < maxItems;

  return (
    <div>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Space wrap>
          {value.map((url, index) => (
            <Badge
              key={index}
              count={
                !disabled ? (
                  <CloseCircleTwoTone onClick={() => handleRemove(url)} twoToneColor="#ff4d4f" />
                ) : (
                  0
                )
              }
            >
              <a href={url} target="_blank" rel="noopener noreferrer">
                <Avatar
                  shape="square"
                  size={64}
                  src={url}
                  icon={<PictureOutlined />}
                  style={{ cursor: 'pointer' }}
                />
              </a>
            </Badge>
          ))}
        </Space>

        {!disabled && canAddMore && (
          <MediaPicker
            uploadConfig={uploadConfig}
            multiple={true}
            onFilesSelect={handleFilesSelect}
            buttonProps={{
              type: 'default',
              style: { width: '100%' },
            }}
          />
        )}
      </Space>
    </div>
  );
};

export default MediaPickerField;
